import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { hardNavigate, postLogoutPath } from '../utils/postLogoutNavigation';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [serverClockOffsetMs, setServerClockOffsetMs] = useState(0);

  const applyServerClock = (payload) => {
    if (!payload?.serverNow) return;
    const serverMs = Date.parse(payload.serverNow);
    if (!Number.isFinite(serverMs)) return;
    setServerClockOffsetMs(serverMs - Date.now());
  };

  const getServerLocalHour = useCallback(() => {
    return new Date(Date.now() + serverClockOffsetMs).getHours();
  }, [serverClockOffsetMs]);

  const fetchPermissions = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users/me/permissions`, {
        withCredentials: true
      });
      setPermissions(response.data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (!user) return undefined;

    const refreshWhenActive = () => {
      if (document.visibilityState === 'visible') {
        fetchPermissions();
      }
    };
    window.addEventListener('focus', refreshWhenActive);
    document.addEventListener('visibilitychange', refreshWhenActive);
    const interval = window.setInterval(fetchPermissions, 60000);

    return () => {
      window.removeEventListener('focus', refreshWhenActive);
      document.removeEventListener('visibilitychange', refreshWhenActive);
      window.clearInterval(interval);
    };
  }, [user?.id, fetchPermissions]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      applyServerClock(response.data);
      setUser(response.data);
      // Fetch permissions after authenticating
      try {
        const permResponse = await axios.get(`${API_URL}/api/users/me/permissions`, {
          withCredentials: true
        });
        setPermissions(permResponse.data);
      } catch (permError) {
        console.error('Error fetching permissions:', permError);
      }
    } catch (error) {
      setUser(null);
      setPermissions(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        credentials,
        { withCredentials: true }
      );

      // If MFA is required, return the response data without setting user
      if (response.data.mfaRequired) {
        return response.data;
      }

      applyServerClock(response.data);
      const userData = response.data.user;
      setUser(userData);

      // Only fetch permissions if user doesn't need to change password
      if (!userData.mustChangePassword) {
        try {
          const permResponse = await axios.get(`${API_URL}/api/users/me/permissions`, {
            withCredentials: true
          });
          setPermissions(permResponse.data);
        } catch (permError) {
          console.error('Error fetching permissions:', permError);
        }
      }
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  };

  const beginMfaSetup = async (setupToken) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login/mfa-setup/begin`,
        { setupToken },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to begin MFA setup');
    }
  };

  const verifyAndCompleteMfaSetup = async (setupToken, token) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login/mfa-setup/verify`,
        { setupToken, token },
        { withCredentials: true }
      );
      const userData = response.data.user;
      setUser(userData);
      if (!userData.mustChangePassword) {
        try {
          const permResponse = await axios.get(`${API_URL}/api/users/me/permissions`, { withCredentials: true });
          setPermissions(permResponse.data);
        } catch (permError) {
          console.error('Error fetching permissions:', permError);
        }
      }
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'MFA setup verification failed');
    }
  };

  const verifyMfaLogin = async (mfaToken, token) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login/mfa`,
        { mfaToken, token },
        { withCredentials: true }
      );

      const userData = response.data.user;
      setUser(userData);

      if (!userData.mustChangePassword) {
        try {
          const permResponse = await axios.get(`${API_URL}/api/users/me/permissions`, {
            withCredentials: true
          });
          setPermissions(permResponse.data);
        } catch (permError) {
          console.error('Error fetching permissions:', permError);
        }
      }
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'MFA verification failed');
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.post(
        `${API_URL}/api/auth/change-password`,
        { currentPassword, newPassword },
        { withCredentials: true }
      );
      // Update user to clear mustChangePassword
      setUser(prev => prev ? { ...prev, mustChangePassword: false } : prev);
      // Fetch permissions now that password is changed
      try {
        const permResponse = await axios.get(`${API_URL}/api/users/me/permissions`, {
          withCredentials: true
        });
        setPermissions(permResponse.data);
      } catch (permError) {
        console.error('Error fetching permissions:', permError);
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to change password');
    }
  };

  const logout = async () => {
    let behindGateway = false;
    try {
      const response = await axios.post(`${API_URL}/api/auth/logout`, {}, {
        withCredentials: true
      });
      behindGateway = response.data?.gateway === true;
    } catch (error) {
      console.error('Logout error:', error);
      behindGateway = error.response?.data?.gateway === true;
    }
    // Full page load so the RadioCloud gateway can serve its login SPA.
    // Client-side navigation to /login would keep the tenant RadioSuiteX form.
    hardNavigate(postLogoutPath(behindGateway));
    return { gateway: behindGateway };
  };

  // Permission helper functions
  const isSuperAdmin = () => user?.role === 'superadmin';
  const isAdmin = () => user?.role === 'admin' || user?.role === 'superadmin';
  const isRegularUser = () => user?.role === 'user';

  const hasModuleAccess = (moduleName) => {
    if (!user) return false;
    // Settings always available to admin/superadmin (station & app configuration lives there)
    if (moduleName === 'settings' && (isSuperAdmin() || isAdmin())) return true;
    if (moduleName === 'stations') return isSuperAdmin();
    if (!permissions) {
      if (isSuperAdmin()) return true;
      if (isAdmin()) {
        if (['ai_content', 'ai_features', 'banned_words'].includes(moduleName)) {
          return user.aiContentEnabled === true || user.ai_content_enabled === true;
        }
        if (moduleName === 'logger') {
          return user.loggerEnabled === true || user.logger_enabled === true;
        }
        if (moduleName === 'intelligence') {
          return user.intelligenceEnabled === true || user.intelligence_enabled === true;
        }
        return moduleName !== 'stations'
          && !['ai_content', 'ai_features', 'banned_words', 'logger', 'intelligence'].includes(moduleName);
      }
      return false;
    }
    if (moduleName === 'intelligence') {
      if (!permissions.modules?.intelligence?.can_view) return false;
      if (isSuperAdmin()) return true;
      return permissions.intelligence_enabled === true;
    }
    // System-gated modules are omitted from /me/permissions when disabled
    return !!permissions.modules?.[moduleName]?.can_view;
  };

  const hasStationManagement = () => {
    return isSuperAdmin();
  };

  const hasGrokApiKey = () => permissions?.has_grok_api_key === true;

  const hasSystemFeature = (featureName) => {
    if (featureName === 'ai_insights' && !hasGrokApiKey()) return false;
    return permissions?.system_features?.[featureName] !== false;
  };

  const hasAiContent = () => {
    if (!user) return false;
    if (!permissions) {
      if (isSuperAdmin()) return true;
      return isAdmin()
        && (user.aiContentEnabled === true || user.ai_content_enabled === true);
    }
    if (!permissions?.modules?.ai_content?.can_view) return false;
    if (isSuperAdmin()) return true;
    return permissions.ai_content_enabled === true;
  };

  const hasLogger = () => {
    if (!user) return false;
    if (!permissions) {
      if (isSuperAdmin()) return true;
      return isAdmin()
        && (user.loggerEnabled === true || user.logger_enabled === true);
    }
    if (!permissions?.modules?.logger?.can_view) return false;
    if (isSuperAdmin()) return true;
    return permissions.logger_enabled === true;
  };

  const hasIntelligence = () => {
    if (!user) return false;
    if (!hasSystemFeature('intelligence')) return false;
    return hasModuleAccess('intelligence');
  };

  // Audio processing: role gate; module-enabled check is in Settings / system_features
  const hasAudioProcessing = () => {
    if (!(isSuperAdmin() || isAdmin())) return false;
    if (permissions?.system_features && permissions.system_features.audio_processing === false) {
      return false;
    }
    return true;
  };

  const getModulePermission = (moduleName) => {
    if (!permissions) return null;
    if (moduleName === 'stations') {
      return isSuperAdmin()
        ? { can_view: true, can_create: true, can_edit: true, can_delete: true, can_manage: true, allowed_hours: null }
        : null;
    }
    const fromMap = permissions.modules?.[moduleName];
    if (!fromMap) return null;
    if (isSuperAdmin() || isAdmin()) {
      return { can_view: true, can_create: true, can_edit: true, can_delete: true, can_manage: true, allowed_hours: null };
    }
    return fromMap;
  };

  const getHourRestrictions = (moduleName) => {
    const perm = getModulePermission(moduleName);
    return perm?.allowed_hours ?? null; // null means no restrictions; [] denies all
  };

  return (
    <AuthContext.Provider value={{
      user,
      permissions,
      isLoading,
      login,
      verifyMfaLogin,
      beginMfaSetup,
      verifyAndCompleteMfaSetup,
      logout,
      checkAuth,
      changePassword,
      refreshPermissions: fetchPermissions,
      isSuperAdmin,
      isAdmin,
      isRegularUser,
      hasModuleAccess,
      hasStationManagement,
      hasSystemFeature,
      hasGrokApiKey,
      hasAiContent,
      hasLogger,
      hasIntelligence,
      hasAudioProcessing,
      getModulePermission,
      getHourRestrictions,
      getServerLocalHour,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
